import { Injectable, TemplateRef } from '@angular/core';
import { EmailContract, EmailListItemContract } from '@app-types/api/email';
import { DrawerService } from '@app-services/drawer.service';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { ComponentType } from '@angular/cdk/overlay';
import { EmailsService } from '@app-services/emails/emails.service';
import { EmailState } from '@app-types/enums/email-state';
import { MailFolderType } from '@app-types/enums/mail-folder.type';
import { ReassignDialogComponent } from '@app-components/reassign-dialog/reassign-dialog.component';
import { MailFolderSelectDialogComponent } from '@app-components/mail-account-folders/mail-folder-select-dialog/mail-folder-select-dialog.component';
import { DeleteDialogComponent } from '@app-components/common/delete-dialog/delete-dialog.component';
import { EmailClient } from '@app-services/api/clients/email-client';
import { UserWorkspaceService } from '@app-services/user-workspace-service';
import { MatchError } from '@app-services/errors/error-matcher';
import { EmailActionsContextService } from '@app-services/emails/button-actions/email-actions-context.service';
import { EmailsPageStateService } from '@app-services/emails/emails-page-state.service';
import { EmailTab } from '@app-types/enums/email-tab';
import { ConfirmDialogComponent } from '@app-components/common/confirm-dialog/confirm-dialog.component';
import { ArchiveState } from '@app-types/enums/archiving';

@Injectable({
  providedIn: 'root',
})
export class ActionsModalsService {
  private readonly emailsService: EmailsService;
  private readonly emailClient: EmailClient;
  public readonly userWorkspaceService: UserWorkspaceService;
  private readonly matchError: MatchError;
  private readonly emailsPageStateService: EmailsPageStateService;

  createOrUpdateDrawer: TemplateRef<any>;
  auditDrawer: TemplateRef<any>;
  metaDrawer: TemplateRef<any>;
  addGroupDrawer: TemplateRef<any>;
  addTagDrawer: TemplateRef<any>;
  emailDrawer: TemplateRef<any>;
  notificationDrawer: TemplateRef<any>;
  notesDrawer: TemplateRef<any>;

  constructor(
    private drawerService: DrawerService,
    private dialog: MatDialog,
    private context: EmailActionsContextService
  ) {
    this.emailsService = context.emailsService;
    this.emailClient = context.emailClient;
    this.matchError = context.matchError;
    this.userWorkspaceService = context.userWorkspaceService;
    this.emailsPageStateService = context.emailsPageStateService;
  }

  public openAudit(auditMailId: number | null = null): void {
    this.emailsPageStateService.auditMailId = auditMailId
      ? auditMailId
      : this.emailsPageStateService.selection.selected[0];
    this.drawerService.openDrawer(this.auditDrawer, null, 'small');
  }

  public openMeta(id: number): void {
    this.emailsPageStateService.metaMailId = id;
    this.drawerService.openDrawer(this.metaDrawer);
  }

  openAddGroupDialog(email?: EmailListItemContract): void {
    this.emailsPageStateService.emailIdsForGroup = email
      ? [email.emailId]
      : this.emailsPageStateService.selection.selected;
    this.drawerService.openDrawer(this.addGroupDrawer, null, 'small');
  }

  openAddTagDialog(email?: EmailListItemContract): void {
    const ids = email
      ? [email.emailId]
      : this.emailsPageStateService.selection.selected;
    const emails = email ? [email] : this.emailsPageStateService.getEmails(ids);

    this.emailsPageStateService.emailsForTags = {
      ids,
      emails,
      mailAccountId: this.emailsPageStateService.infoOpenedMailAccount.acc,
    };
    this.drawerService.openDrawer(this.addTagDrawer, null, 'small');
  }

  public openNotifications(id: number): void {
    this.emailsPageStateService.notificationMailId = id;
    this.drawerService.openDrawer(this.notificationDrawer, null, 'small');
  }

  public openNotes(id: number): void {
    this.emailsPageStateService.notesMailId = id;
    this.drawerService.openDrawer(this.notesDrawer, null, 'small');
  }

  public editEmail(email: EmailListItemContract): void {
    this.openCreateOrUpdateModalForm(email);
  }

  async emailDetails(row: EmailListItemContract): Promise<void> {
    if (
      row.state === EmailState.Draft ||
      row.state === EmailState.ValidationRejected ||
      this.emailsPageStateService.infoOpenedMailAccount?.type ===
        MailFolderType.Draft
    ) {
      this.editEmail(row);
      return;
    }
    this.openEmailDetails(row);
  }

  public openEmailDetails(email: EmailListItemContract): void {
    this.emailsPageStateService.openedEmail = email;
    this.drawerService.closeDrawer();
    this.drawerService.openDrawer(this.emailDrawer, null, 'create-email');
  }

  public openCreateOrUpdateModalForm(
    email: EmailContract | EmailListItemContract | null
  ): void {
    this.emailsPageStateService.updatedEmail = email;
    this.drawerService.openDrawer(
      this.createOrUpdateDrawer,
      null,
      'create-email'
    );
  }

  openDialog(
    component: ComponentType<any>,
    config: MatDialogConfig,
    callback?: (e: any) => void
  ): void {
    const dialogRef = this.dialog.open(component, config);
    dialogRef.afterClosed().subscribe(callback);
  }

  async reassign(id?: number): Promise<any> {
    const ids = id ? [id] : this.emailsPageStateService.selection.selected;
    this.openDialog(
      ReassignDialogComponent,
      {
        width: '350px',
        autoFocus: false,
        data: {
          mailAccountId: this.emailsPageStateService.infoOpenedMailAccount.acc,
          ids,
        },
      },
      async (e: { confirmed: boolean; mailAccounts?: number[] }) => {
        if (e?.confirmed) {
          const currentTab =
            this.emailsPageStateService.infoOpenedMailAccount?.tab ??
            EmailTab.NewAndValidatedUnreadCount;

          const selectedEmails =
            this.emailsPageStateService.dataSource.data.filter(el =>
              ids.includes(el.emailId)
            );

          selectedEmails.forEach(el => {
            if (!el.seen) {
              this.userWorkspaceService.updateEmailTabBadges(currentTab, false);
              this.userWorkspaceService.updateEmailTabBadges(
                EmailTab.ReassignedUnreadCount,
                true
              );
            }
          });

          this.emailsService.updateDataOnEmailCountChange(ids);
          if (e.mailAccounts?.length) {
            const mailAccounts = e.mailAccounts.filter(
              el => el !== this.emailsPageStateService.infoOpenedMailAccount.acc
            );
            mailAccounts.forEach(el =>
              this.userWorkspaceService.updateUnreadMailAccBadges(
                el,
                true,
                ids.length
              )
            );
          }
        }
      }
    );
  }

  async move(id: number | null = null): Promise<any> {
    const ids = id ? [id] : this.emailsPageStateService.selection.selected;
    this.openDialog(
      MailFolderSelectDialogComponent,
      {
        width: '350px',
        autoFocus: false,
        data: {
          mailAccountId: this.emailsPageStateService.infoOpenedMailAccount.acc,
          ids,
          isRestore: false,
          folderType: this.emailsPageStateService.infoOpenedMailAccount.type,
        },
      },
      async (e: { confirmed: boolean; folderId: number }) => {
        if (e?.confirmed) {
          if (
            e?.folderId !==
            this.emailsPageStateService.infoOpenedMailAccount?.folder
          ) {
            this.userWorkspaceService.updateMoveList(ids, e?.folderId);

            this.emailsService.updateDataOnEmailCountChange(ids);
          }
        }
      }
    );
  }

  async setSelectedArchive(id: number | null = null): Promise<any> {
    const ids = id ? [id] : this.emailsPageStateService.selection.selected;
    this.openDialog(
      MailFolderSelectDialogComponent,
      {
        width: '350px',
        autoFocus: false,
        data: {
          mailAccountId: this.emailsPageStateService.infoOpenedMailAccount.acc,
          ids,
          isRestore: true,
          folderType: this.emailsPageStateService.infoOpenedMailAccount.type,
          isArchive: true,
          sourceFoldersType: MailFolderType.Archive,
          title: 'moveToArchive',
        },
      },
      async (e: { confirmed: boolean; folderId: number }) => {
        if (e?.confirmed) {
          try {
            await this.emailClient.archive({
              emailIds: ids,
              mailFolderId: e.folderId,
            });
            this.userWorkspaceService.updateMoveList(ids, e.folderId);
            this.emailsService.updateDataOnEmailCountChange(ids);
            // this.emailsService.reloadDataOnDemand();
          } catch (e) {
            this.matchError.errorHandler(e);
            this.matchError.logError(e);
          }
        }
      }
    );
  }

  async restoreFromArchive(id: number | null = null): Promise<any> {
    const ids = id ? [id] : this.emailsPageStateService.selection.selected;
    const emails = this.emailsPageStateService.getEmails(ids);
    if (
      !emails.every(
        (e, i, arr) => arr[0].sourceFolderType === e.sourceFolderType
      )
    ) {
      this.matchError.openErrorDialog(
        'errorPopupTitle',
        'sourceFoldersMustBeTheSameErrorPopupSubtitle'
      );
      return null;
    }
    if (ids.length > 0) {
      this.openDialog(
        MailFolderSelectDialogComponent,
        {
          width: '350px',
          autoFocus: false,
          data: {
            mailAccountId:
              this.emailsPageStateService.infoOpenedMailAccount.acc,
            ids,
            isRestore: true,
            folderType: this.emailsPageStateService.infoOpenedMailAccount.type,
            isArchive: true,
            sourceFoldersType: emails[0].sourceFolderType,
            title: 'restoreFromArchive',
          },
        },
        async (e: { confirmed: boolean; folderId: number }) => {
          if (e?.confirmed) {
            try {
              await this.emailClient.restoreFromArchive({
                emailIds: ids,
                mailFolderId: e.folderId,
              });
              this.userWorkspaceService.updateMoveList(ids, e.folderId);
              this.emailsService.updateDataOnEmailCountChange(ids);
              // this.emailsService.reloadDataOnDemand();
            } catch (e) {
              this.matchError.errorHandler(e);
              this.matchError.logError(e);
            }
          }
        }
      );
    }
  }

  async restoreFromTrash(emailId?: number): Promise<any> {
    const ids = emailId
      ? [emailId]
      : this.emailsPageStateService.selection.selected;
    const emails = this.emailsPageStateService.getEmails(ids);
    if (
      !emails.every(
        (e, i, arr) => arr[0].sourceFolderType === e.sourceFolderType
      )
    ) {
      this.matchError.openErrorDialog(
        'error',
        'sourceFoldersMustBeTheSameErrorPopupSubtitle'
      );
      return null;
    }
    if (ids.length > 0) {
      this.openDialog(
        MailFolderSelectDialogComponent,
        {
          width: '350px',
          autoFocus: false,
          data: {
            mailAccountId:
              this.emailsPageStateService.infoOpenedMailAccount.acc,
            ids,
            isRestore: true,
            folderType: this.emailsPageStateService.infoOpenedMailAccount.type,
            isTrash: true,
            sourceFoldersType: emails[0].sourceFolderType,
            title: 'restoreEmail',
          },
        },
        async (e: { confirmed: boolean; folderId: number }) => {
          if (e?.confirmed) {
            try {
              await this.emailClient.restore({
                emailIds: ids,
                mailFolderId: e.folderId,
              });
              this.userWorkspaceService.updateMoveList(ids, e.folderId);
              this.emailsService.updateDataOnEmailCountChange(ids);
              // this.emailsService.reloadDataOnDemand();
            } catch (e) {
              this.matchError.errorHandler(e);
              this.matchError.logError(e);
            }
          }
        }
      );
    }
  }

  async onFinalDelete(ids: number[]): Promise<void> {
    const result = await this.emailClient.finalDelete({ emailIds: ids });
    if (result.countUnsuccessDelete && result.countUnsuccessDelete > 0) {
      this.matchError.openErrorDialog(
        'error',
        'messagesNotDeleted',
        result.countUnsuccessDelete
      );
    }
    this.emailsPageStateService.successDeleteEmailIds =
      result.successDeleteEmailIds ?? [];
  }

  async finalDelete(id?: number): Promise<any> {
    const ids = id ? [id] : this.emailsPageStateService.selection.selected;
    this.openDialog(
      DeleteDialogComponent,
      {
        width: '450px',
        autoFocus: false,
        data: {
          title: 'permanentlyDelete',
          subTitles: [
            {
              title: 'attention',
              subTitle: 'selectedMessagePermanentlyDeleted',
            },
            {
              title: 'trashUpperCase',
              subTitle: 'andNoPossibleRecover',
            },
          ],
          onDelete: async () => await this.onFinalDelete(ids),
        },
      },
      x => {
        if (x?.isDeleted) {
          if (this.emailsPageStateService.successDeleteEmailIds?.length) {
            this.userWorkspaceService.updateMoveList(ids);
            this.emailsService.updateDataOnEmailCountChange(ids);
            // this.emailsService.reloadDataOnDemand();
            this.emailsPageStateService.successDeleteEmailIds = [];
          }
        }
      }
    );
  }

  async onArchiving(ids: number[]): Promise<any> {
    await this.emailClient.archiving({ list: ids });
  }

  archiving(id?: number): any {
    const ids = id ? [id] : this.emailsPageStateService.selection.selected;
    this.openDialog(
      ConfirmDialogComponent,
      {
        width: '450px',
        autoFocus: false,
        data: {
          title: 'sendingInArchive',
          name: 'confirmSendingInArchive',
          isDontNeedConfirmWord: true,
          submit: async () => await this.onArchiving(ids),
        },
      },
      result => {
        result &&
          this.emailsPageStateService.changeDataTable(
            ids,
            'externalArchiveState',
            ArchiveState.NotReadyToCheck
          );
      }
    );
  }

  async onDelete(ids: number[]): Promise<any> {
    await this.emailClient.delete({ emailIds: ids });
  }

  updateDataAfterDelete(ids: number[]): void {
    const folderTrashId = this.userWorkspaceService.userFolders.find(
      el =>
        el.mailAccountId ===
          this.emailsPageStateService.infoOpenedMailAccount?.acc &&
        el.folderType === MailFolderType.Trash
    )?.mailFolderId;

    this.userWorkspaceService.updateMoveList(ids, folderTrashId);
    this.emailsService.updateDataOnEmailCountChange(ids);
  }

  async delete(id?: number): Promise<any> {
    const ids = id ? [id] : this.emailsPageStateService.selection.selected;
    try {
      const emails = this.emailsPageStateService.getEmails(ids);
      const states = [ArchiveState.Off, ArchiveState.Archived];
      const nedConfirm = emails.every(
        el =>
          (el.externalArchiveState ||
            el.externalArchiveState === ArchiveState.NotReadyToCheck) &&
          !states.includes(el.externalArchiveState)
      );

      if (nedConfirm) {
        this.openDialog(
          DeleteDialogComponent,
          {
            width: '450px',
            autoFocus: false,
            data: {
              title: 'deleteConfirmTitle',
              subTitles: [
                {
                  title: 'attention',
                  subTitle: 'deleteEmailConfirmSubTitle',
                },
              ],
              onDelete: async () => await this.onDelete(ids),
            },
          },
          x => {
            if (x?.isDeleted) {
              this.updateDataAfterDelete(ids);
            }
          }
        );

        return;
      }

      await this.onDelete(ids);
      this.updateDataAfterDelete(ids);
    } catch (e) {
      this.matchError.errorHandler(e);
      this.matchError.logError(e);
    }
  }
}
